<template>
  <div class="settings-item">
    <InputSearchSecondary
      v-if="props.isOpenList"
      v-model.trim="store.filterRegion.inputValue"
      :placeholder="t('settings_item.placeholder.search')"
    />

    <ul
      v-if="props.isOpenList && hasCities"
      class="settings-item__list"
      :class="{ scroll: isShowAllCities}"
    >
      <li>
        <Checkbox
          :model-value="isAllCitiesSelected"
          class="settings-item__list-item"
          :class="{'settings-item__list-item--selected': isAllCitiesSelected}"
          :value="'all'"
          :label="t('settings_item.placeholder.all')"
          @update:model-value="toggleCities(true)"
        />
      </li>
      <ListItem
        v-for="listItem in displayCitiesList"
        :key="listItem.id"
        v-model="store.filterRegion.filterList"
        :item="listItem.id"
        :use-checkbox="true"
        :disabled="isCityDisabled(listItem)"
        @update:model-value="toggleCities(false)"
      >
        {{ listItem.city }}
      </ListItem>
    </ul>

    <ButtonRoundedPrimary
      v-if="hasMoreThanThreeCities && props.isOpenList"
      size="small"
      @click="toggleVisible"
    >
      {{ buttonLabel }}
    </ButtonRoundedPrimary>
  </div>
</template>

<script setup>
import {ref, defineProps, computed, toRef} from "vue";
import { useNewsFeedStore } from "@/store/NewsFeed/newsFeed";
import { t } from "@/controllerLK/AvalLocalization";
import {debounce} from "@/functions/debouncer";

const props = defineProps({
  isOpenList: {
    type: Boolean,
    default: false,
  }
});

const store = useNewsFeedStore();

// Значение поисковой строки
const searchValue = toRef(store.filterRegion, "inputValue");

// Количество отображаемых городов.
const showIndex = 3;

// Флаг. Отображаеть
const isShowAllCities = ref(false);

// Флаг. Выбраны ли все города из списка.
const isAllCitiesSelected = computed(() => store.filterRegion.filterList.length === 0);

// Флаг запрета выбора городов во время загрузки.
// Для предотвращения выбора выключенных городов в параметере "И"
const isLoadingCities = ref(false);

const hasCities = computed(() => store.filterRegion.list?.length > 0);
const hasMoreThanThreeCities = computed(() => store.filterRegion.list?.length > 3);

// Функция выбора городов.
const toggleCities = async (isAll = false) => {
  if (isAll) {
    store.filterRegion.filterList = [];
  }

  await updateNewsDebounce();
};

// Вычисляемая функция. Возвращает список городов в отсортированном порядке.
// Сначала все доступные города, затем все остальные.
const sortedCities = computed(() => {
  if (store.filterRegion.condition) {

    const list1Set = new Set( store.facetParams.cities.map(item => item.id));
    const presentInList1 = store.filterRegion.list.filter(item => list1Set.has(item.id));
    const notInList1 = store.filterRegion.list.filter(item => !list1Set.has(item.id));

    presentInList1.sort((a, b) => a.city.localeCompare(b.city));
    notInList1.sort((a, b) => a.city.localeCompare(b.city));

    return [...presentInList1, ...notInList1];
  } else {
    return store.filterRegion.list;
  }
});

// Вычисляемая функция списка отображаемых городов
const displayCitiesList = computed(() => {
  const citiesList = sortedCities.value.filter((item) => {
    return item.city.toLowerCase().startsWith(searchValue.value.toLowerCase());
  });

  if (isShowAllCities.value) {
    return citiesList;
  } else {
    return citiesList.slice(0, showIndex);
  }
});

// Вычисляемая функция проверки, является ли город доступ для выбора.
// В зависимости от выбора переключателя И/ИЛИ
const isCityDisabled = (listItem) => {
  if (store.filterRegion.condition) {
    return !!store.facetParams && !store.facetParams?.cities?.find(item => item.city === listItem.city);
  } else {
    return false;
  }
};

// Вычисляемая функция получения лейбла для кнопки раскрытия списка городов.
const buttonLabel = computed(() => {
  return isShowAllCities.value ? t("settings_item.button.collapse") : t("settings_item.button.see_all");
});

// Функция раскрытия списка городов
function toggleVisible() {
  isShowAllCities.value = !isShowAllCities.value;
}

// Функция обновления списка новостей.
const updateNews = async () => {
  isLoadingCities.value = true;
  store.clearNewsFeed();
  await store.getNews();
  isLoadingCities.value = false;
};
const updateNewsDebounce = debounce(updateNews, 700);
</script>

<style lang="scss" scoped>

.settings-item {
  display: flex;
  flex-direction: column;
  gap: 10px;

  &__list {
    &.scroll {
      max-height: 300px;
      overflow-x: hidden;
    }
  }

  &__list-item {
    border-radius: 8px;
    padding: 6px 14px;
    height: 30px;
    cursor: pointer;
    width: 100%;

    &--selected {
      :deep(.checkbox__label) { color: var(--text-text-on); }
      &:hover { background-color: var(--background-bg-active-hover); }
    }

    &:hover:not(&--selected) {
      background: var(--background-bg-hover);
    }
  }
}

</style>